/**
 * The icon catalogue for the Design Builder, built on **lucide-angular**.
 *
 * ## Why Lucide
 *
 * The requirement was a configurable **stroke**, and that rules out most of the
 * field: icon fonts (including the Material Icons this app already uses) and
 * filled-only sets have no stroke to configure — you get colour and size and
 * nothing else. Lucide is stroke-first: every glyph is a 24×24 SVG drawn with
 * `stroke-width`, and `lucide-angular` exposes `color` / `size` / `strokeWidth`
 * / `absoluteStrokeWidth` as component inputs, which is exactly the control
 * surface a design tool needs.
 *
 * The Angular alternatives were checked and do not fit this workspace:
 *
 * | Package | Verdict |
 * |---|---|
 * | `lucide-angular` | **chosen** — Angular 13–21 peer range, MIT, stroke-first |
 * | `angular-tabler-icons` | peer range is Angular 17–19; will not install on 20 |
 * | `@ng-icons/core` | requires Angular ≥ 22 |
 * | `@phosphor-icons/angular` | does not exist on npm |
 *
 * ## Why a curated list
 *
 * Lucide ships ~1700 icons. Importing the whole barrel would defeat
 * tree-shaking and bury the useful ones under hundreds of irrelevant glyphs, so
 * this file names the icons a dashboard/report designer actually reaches for,
 * grouped for browsing. Each is a named import, so only these land in the
 * bundle. Adding one is a single line.
 *
 * Icons are stored in the document **by name**, not as inlined SVG: the JSON
 * stays small and re-styling (colour, stroke) keeps working after a reload.
 * `iconDataFor()` resolves a stored name back to its drawing data at render
 * time.
 */

import {
  // Charts & data
  ChartBar, ChartLine, ChartPie, ChartColumn, Activity, TrendingUp, TrendingDown,
  Gauge, Table, Database, Funnel, Target, Percent, Sigma, ChartScatter, ListFilter,
  // Navigation & layout
  House, LayoutDashboard, LayoutGrid, Layers, Menu, Ellipsis, Grid3x3,
  Columns2, Rows3, Maximize, Minimize, Move, PanelLeft, PanelTop,
  // Actions
  Search, Download, Upload, RefreshCw, RotateCcw, Share2, Printer, Copy, Trash2, Pencil,
  Plus, Minus, Check, X, ExternalLink, Send, Save,
  // Arrows
  ArrowUp, ArrowDown, ArrowLeft, ArrowRight, ChevronRight, ChevronDown,
  ArrowUpRight, ArrowDownRight, Repeat, Shuffle,
  // Status & feedback
  TriangleAlert, Info, CircleCheck, CircleAlert, CircleQuestionMark, CircleDot,
  Bell, Shield, Lock, Eye, EyeOff, Star, Heart, Flag, Award,
  // Business & finance
  DollarSign, CreditCard, ShoppingCart, Package, Truck, Factory, Building2,
  Briefcase, Wallet, Receipt, Coins, PiggyBank, Scale, Tag,
  // People
  User, Users, UserCheck, UserPlus, MessageSquare, Mail, Phone, Inbox,
  // Files & content
  FileText, FileSpreadsheet, FilePlus, Folder, Archive, Clipboard,
  ClipboardList, BookOpen, GraduationCap, Presentation, Bookmark, Link,
  // Time
  Calendar, CalendarDays, Clock, Timer, Hourglass,
  // Tech
  Server, Cloud, Wifi, Cpu, HardDrive, Code, Terminal, GitBranch, Network,
  Monitor, Smartphone, Workflow, Boxes, Component, Puzzle,
  // Media
  Image, Video, Music, Mic, Camera, Play, Pause, Volume2,
  // Design & misc
  Palette, Brush, Type, Sun, Moon, Sparkles, Lightbulb, Rocket, Zap, Key,
  Settings, Cog, SlidersHorizontal, Wrench, Hammer, Compass, MapPin, Globe,
  Navigation, Route, Crosshair, Square, Circle, Triangle, Hexagon,
} from 'lucide-angular';

/** Lucide's icon drawing data. Kept loose so this file needs no deep import. */
export type IconData = unknown;

export interface IconCatalogueEntry {
  /** Stored in the document — PascalCase Lucide name. */
  name: string;
  /** Human label shown under the tile and matched by search. */
  label: string;
  data: IconData;
  /** Extra search terms, so "chart" finds Activity and "delete" finds Trash2. */
  keywords?: string;
}

export interface IconCatalogueGroup {
  key: string;
  label: string;
  entries: IconCatalogueEntry[];
}

/** Terse helper so the groups below stay readable. */
const ic = (name: string, data: IconData, label: string, keywords = ''): IconCatalogueEntry => ({
  name,
  data,
  label,
  keywords,
});

export const ICON_GROUPS: IconCatalogueGroup[] = [
  {
    key: 'charts',
    label: 'Charts & data',
    entries: [
      ic('ChartBar', ChartBar, 'Bar chart', 'graph analytics'),
      ic('ChartColumn', ChartColumn, 'Column chart', 'graph analytics'),
      ic('ChartLine', ChartLine, 'Line chart', 'graph trend'),
      ic('ChartPie', ChartPie, 'Pie chart', 'donut share'),
      ic('ChartScatter', ChartScatter, 'Scatter', 'bubble plot'),
      ic('Activity', Activity, 'Activity', 'pulse trend chart'),
      ic('TrendingUp', TrendingUp, 'Trending up', 'growth increase'),
      ic('TrendingDown', TrendingDown, 'Trending down', 'decline decrease'),
      ic('Gauge', Gauge, 'Gauge', 'kpi meter speed'),
      ic('Sigma', Sigma, 'Sum', 'total aggregate'),
      ic('Percent', Percent, 'Percent', 'ratio share'),
      ic('Table', Table, 'Table', 'grid rows matrix'),
      ic('Database', Database, 'Database', 'data source'),
      ic('Funnel', Funnel, 'Filter', 'funnel refine'),
      ic('ListFilter', ListFilter, 'Filter lines', 'refine sort'),
      ic('Target', Target, 'Target', 'goal objective'),
    ],
  },
  {
    key: 'layout',
    label: 'Layout & navigation',
    entries: [
      ic('House', House, 'Home', 'start landing'),
      ic('LayoutDashboard', LayoutDashboard, 'Dashboard', 'layout tiles'),
      ic('LayoutGrid', LayoutGrid, 'Grid', 'layout tiles'),
      ic('Layers', Layers, 'Layers', 'stack'),
      ic('Grid3x3', Grid3x3, 'Grid 3×3', 'layout'),
      ic('Columns2', Columns2, 'Columns', 'split layout'),
      ic('Rows3', Rows3, 'Rows', 'stack layout'),
      ic('PanelLeft', PanelLeft, 'Side panel', 'sidebar drawer'),
      ic('PanelTop', PanelTop, 'Top panel', 'header bar'),
      ic('Menu', Menu, 'Menu', 'hamburger nav'),
      ic('Ellipsis', Ellipsis, 'More', 'overflow dots'),
      ic('Maximize', Maximize, 'Maximize', 'expand fullscreen'),
      ic('Minimize', Minimize, 'Minimize', 'collapse shrink'),
      ic('Move', Move, 'Move', 'drag reposition'),
    ],
  },
  {
    key: 'actions',
    label: 'Actions',
    entries: [
      ic('Search', Search, 'Search', 'find magnify'),
      ic('Download', Download, 'Download', 'export save'),
      ic('Upload', Upload, 'Upload', 'import'),
      ic('RefreshCw', RefreshCw, 'Refresh', 'reload sync'),
      ic('RotateCcw', RotateCcw, 'Reset', 'clear filters reset dashboard'),
      ic('Share2', Share2, 'Share', 'send distribute'),
      ic('Printer', Printer, 'Print', 'report'),
      ic('Save', Save, 'Save', 'store disk'),
      ic('Copy', Copy, 'Copy', 'duplicate'),
      ic('Pencil', Pencil, 'Edit', 'write modify'),
      ic('Trash2', Trash2, 'Delete', 'remove bin trash'),
      ic('Plus', Plus, 'Add', 'new create'),
      ic('Minus', Minus, 'Remove', 'subtract'),
      ic('Check', Check, 'Check', 'done confirm'),
      ic('X', X, 'Close', 'cancel dismiss'),
      ic('ExternalLink', ExternalLink, 'Open link', 'external'),
      ic('Send', Send, 'Send', 'submit'),
    ],
  },
  {
    key: 'arrows',
    label: 'Arrows',
    entries: [
      ic('ArrowUp', ArrowUp, 'Up', 'increase'),
      ic('ArrowDown', ArrowDown, 'Down', 'decrease'),
      ic('ArrowLeft', ArrowLeft, 'Left', 'back previous'),
      ic('ArrowRight', ArrowRight, 'Right', 'next forward'),
      ic('ArrowUpRight', ArrowUpRight, 'Up right', 'growth'),
      ic('ArrowDownRight', ArrowDownRight, 'Down right', 'decline'),
      ic('ChevronRight', ChevronRight, 'Chevron right', 'expand next'),
      ic('ChevronDown', ChevronDown, 'Chevron down', 'expand dropdown'),
      ic('Repeat', Repeat, 'Repeat', 'cycle recurring'),
      ic('Shuffle', Shuffle, 'Shuffle', 'random mix'),
    ],
  },
  {
    key: 'status',
    label: 'Status & alerts',
    entries: [
      ic('CircleCheck', CircleCheck, 'Success', 'ok done valid'),
      ic('CircleAlert', CircleAlert, 'Alert', 'error warning'),
      ic('TriangleAlert', TriangleAlert, 'Warning', 'caution danger'),
      ic('Info', Info, 'Info', 'information help'),
      ic('CircleQuestionMark', CircleQuestionMark, 'Help', 'question support'),
      ic('CircleDot', CircleDot, 'Status dot', 'indicator'),
      ic('Bell', Bell, 'Notification', 'alert reminder'),
      ic('Shield', Shield, 'Secure', 'protection safety'),
      ic('Lock', Lock, 'Locked', 'secure private'),
      ic('Eye', Eye, 'Visible', 'show view'),
      ic('EyeOff', EyeOff, 'Hidden', 'hide'),
      ic('Star', Star, 'Star', 'favourite rating'),
      ic('Heart', Heart, 'Heart', 'like favourite'),
      ic('Flag', Flag, 'Flag', 'mark report'),
      ic('Award', Award, 'Award', 'achievement badge'),
    ],
  },
  {
    key: 'business',
    label: 'Business & finance',
    entries: [
      ic('DollarSign', DollarSign, 'Currency', 'money revenue price'),
      ic('CreditCard', CreditCard, 'Card', 'payment billing'),
      ic('Wallet', Wallet, 'Wallet', 'budget funds'),
      ic('Coins', Coins, 'Coins', 'money cash'),
      ic('PiggyBank', PiggyBank, 'Savings', 'budget'),
      ic('Receipt', Receipt, 'Receipt', 'invoice bill'),
      ic('ShoppingCart', ShoppingCart, 'Cart', 'orders sales'),
      ic('Package', Package, 'Package', 'product inventory'),
      ic('Truck', Truck, 'Shipping', 'logistics delivery'),
      ic('Factory', Factory, 'Plant', 'manufacturing'),
      ic('Building2', Building2, 'Company', 'office organisation'),
      ic('Briefcase', Briefcase, 'Business', 'work portfolio'),
      ic('Scale', Scale, 'Balance', 'compare weigh'),
      ic('Tag', Tag, 'Tag', 'label category'),
    ],
  },
  {
    key: 'people',
    label: 'People & messaging',
    entries: [
      ic('User', User, 'User', 'person account'),
      ic('Users', Users, 'Users', 'team group'),
      ic('UserCheck', UserCheck, 'Approved user', 'verified'),
      ic('UserPlus', UserPlus, 'Add user', 'invite'),
      ic('MessageSquare', MessageSquare, 'Message', 'comment chat'),
      ic('Mail', Mail, 'Email', 'mail inbox'),
      ic('Phone', Phone, 'Phone', 'call contact'),
      ic('Inbox', Inbox, 'Inbox', 'messages'),
    ],
  },
  {
    key: 'files',
    label: 'Files & content',
    entries: [
      ic('FileText', FileText, 'Document', 'file report'),
      ic('FileSpreadsheet', FileSpreadsheet, 'Spreadsheet', 'excel csv data'),
      ic('FilePlus', FilePlus, 'New file', 'create'),
      ic('Folder', Folder, 'Folder', 'directory'),
      ic('Archive', Archive, 'Archive', 'storage'),
      ic('Clipboard', Clipboard, 'Clipboard', 'paste'),
      ic('ClipboardList', ClipboardList, 'Checklist', 'tasks'),
      ic('BookOpen', BookOpen, 'Guide', 'docs manual'),
      ic('GraduationCap', GraduationCap, 'Learn', 'training'),
      ic('Presentation', Presentation, 'Presentation', 'slides deck'),
      ic('Bookmark', Bookmark, 'Bookmark', 'saved'),
      ic('Link', Link, 'Link', 'url reference'),
    ],
  },
  {
    key: 'time',
    label: 'Time',
    entries: [
      ic('Calendar', Calendar, 'Calendar', 'date schedule'),
      ic('CalendarDays', CalendarDays, 'Date range', 'period'),
      ic('Clock', Clock, 'Clock', 'time hour'),
      ic('Timer', Timer, 'Timer', 'duration countdown'),
      ic('Hourglass', Hourglass, 'Pending', 'waiting'),
    ],
  },
  {
    key: 'tech',
    label: 'Tech & systems',
    entries: [
      ic('Server', Server, 'Server', 'backend host'),
      ic('Cloud', Cloud, 'Cloud', 'hosting saas'),
      ic('Wifi', Wifi, 'Network', 'connection'),
      ic('Cpu', Cpu, 'Processing', 'compute'),
      ic('HardDrive', HardDrive, 'Storage', 'disk'),
      ic('Code', Code, 'Code', 'development'),
      ic('Terminal', Terminal, 'Terminal', 'console cli'),
      ic('GitBranch', GitBranch, 'Branch', 'version git'),
      ic('Network', Network, 'Network', 'topology nodes'),
      ic('Workflow', Workflow, 'Workflow', 'pipeline process'),
      ic('Boxes', Boxes, 'Modules', 'components'),
      ic('Component', Component, 'Component', 'widget'),
      ic('Puzzle', Puzzle, 'Integration', 'plugin'),
      ic('Monitor', Monitor, 'Desktop', 'screen'),
      ic('Smartphone', Smartphone, 'Mobile', 'phone device'),
    ],
  },
  {
    key: 'media',
    label: 'Media',
    entries: [
      ic('Image', Image, 'Image', 'photo picture'),
      ic('Video', Video, 'Video', 'film'),
      ic('Camera', Camera, 'Camera', 'capture'),
      ic('Music', Music, 'Music', 'audio'),
      ic('Mic', Mic, 'Microphone', 'record voice'),
      ic('Play', Play, 'Play', 'start'),
      ic('Pause', Pause, 'Pause', 'stop'),
      ic('Volume2', Volume2, 'Volume', 'sound audio'),
    ],
  },
  {
    key: 'misc',
    label: 'Design & shapes',
    entries: [
      ic('Palette', Palette, 'Palette', 'colour theme'),
      ic('Brush', Brush, 'Brush', 'paint style'),
      ic('Type', Type, 'Text', 'font typography'),
      ic('Sun', Sun, 'Light', 'day theme'),
      ic('Moon', Moon, 'Dark', 'night theme'),
      ic('Sparkles', Sparkles, 'AI', 'magic generate'),
      ic('Lightbulb', Lightbulb, 'Idea', 'insight tip'),
      ic('Rocket', Rocket, 'Launch', 'deploy release'),
      ic('Zap', Zap, 'Fast', 'power energy'),
      ic('Key', Key, 'Key', 'access credential'),
      ic('Settings', Settings, 'Settings', 'config preferences'),
      ic('Cog', Cog, 'Configure', 'gear options'),
      ic('SlidersHorizontal', SlidersHorizontal, 'Controls', 'tune adjust'),
      ic('Wrench', Wrench, 'Maintenance', 'tools fix'),
      ic('Hammer', Hammer, 'Build', 'construct'),
      ic('Compass', Compass, 'Explore', 'discover'),
      ic('MapPin', MapPin, 'Location', 'place map'),
      ic('Globe', Globe, 'Global', 'world region'),
      ic('Navigation', Navigation, 'Navigate', 'direction'),
      ic('Route', Route, 'Route', 'path journey'),
      ic('Crosshair', Crosshair, 'Precision', 'focus aim'),
      ic('Square', Square, 'Square', 'shape'),
      ic('Circle', Circle, 'Circle', 'shape dot'),
      ic('Triangle', Triangle, 'Triangle', 'shape'),
      ic('Hexagon', Hexagon, 'Hexagon', 'shape'),
    ],
  },
];

/** Flat lookup of every catalogued icon, keyed by its stored name. */
export const ICON_BY_NAME: ReadonlyMap<string, IconCatalogueEntry> = new Map(
  ICON_GROUPS.flatMap((group) => group.entries).map((entry) => [entry.name, entry]),
);

/** Default icon for a freshly dropped icon node. */
export const DEFAULT_ICON_NAME = 'Star';

/**
 * Drawing data for a stored icon name, or the default when the name is unknown
 * — a design referencing an icon that was later removed from the catalogue
 * still renders something rather than collapsing to an empty box.
 */
export function iconDataFor(name: string): IconData {
  return (ICON_BY_NAME.get(name) ?? ICON_BY_NAME.get(DEFAULT_ICON_NAME))?.data ?? null;
}

/** Catalogue filtered by a free-text query across label, name and keywords. */
export function searchIcons(query: string): IconCatalogueGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return ICON_GROUPS;

  return ICON_GROUPS.map((group) => ({
    ...group,
    entries: group.entries.filter(
      (entry) =>
        entry.label.toLowerCase().includes(needle) ||
        entry.name.toLowerCase().includes(needle) ||
        (entry.keywords ?? '').includes(needle),
    ),
  })).filter((group) => group.entries.length > 0);
}
